// src/components/ReusableFragments.js
import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import CommonFunctions from './CommonFunctions';
import Slider from 'react-slick'; // Imported directly for birthdays; could be lazy-loaded if needed

// Reusable Navigation Fragment
export const RenderNavigation = () => {
  const Navigationbar = React.lazy(() => import('./Navigationbar'));
  return (
    <Suspense fallback={<div>Loading Navigation...</div>}>
      <Navigationbar />
    </Suspense>
  );
};

// Reusable Footer Fragment
export const RenderFooter = () => {
  const Footer = React.lazy(() => import('./Footer'));
  return (
    <Suspense fallback={<div>Loading Footer...</div>}>
      <Footer />
    </Suspense>
  );
};

// Reusable Left Aside (Homepage-specific for now: Upcoming Matches and Top Players)
export const RenderLeftAside = ({ upcomingMatches, resultedMatches, topBatters, topBowlers }) => {
  const renderUpcomingMatches = () => {
    if (!upcomingMatches?.length) {
      return <li className="list-group-item px-2">No More Matches. Wait for the next season.</li>;
    }
    return upcomingMatches.map((match) => (
      <li className="list-group-item px-2" key={match.match_id}>
        <div className="team-title">
          <div className="d-flex align-items-top">
            <div className="flex-grow ms-2">
              <div className="match-date">
                {match.formatted_match_no === '21st' ? "Eliminator" : match.formatted_match_no === '22nd' ? "Finals" : "Match No.: " + match.formatted_match_no}
              </div>
              <div>
                <Link to={`/match/${match.match_id}/${match.team1_shortcode.toLowerCase()}-vs-${match.team2_shortcode.toLowerCase()}`}>
                  <strong title={match.team1_name}>{match.team1_shortcode}</strong>
                  <small className="text-muted px-1">[vs]</small>
                  <strong title={match.team2_name}>{match.team2_shortcode}</strong>
                </Link>
              </div>
              <div className="match-venue">{match.venue_name}, {match.city}</div>
              <small>{match.match_date} {match.match_time}</small>
            </div>
          </div>
        </div>
      </li>
    ));
  };

  const renderMatches = () => {
    if (!resultedMatches?.length) {
      return <li className="list-group-item px-2">No More Matches. Wait for the next season.</li>;
    }
    return resultedMatches.map((match) => (
      <li className="list-group-item px-2" key={match.match_id}>
        <div className="team-title">
          <div className="d-flex align-items-top">
            <div className="flex-grow ms-2">
              <div className="match-date">
                {match.formatted_match_no === '21st' ? "Eliminator" : match.formatted_match_no === '22nd' ? "Finals" : "Match No.: " + match.formatted_match_no}
              </div>
              <div>
                <Link to={`/match/${match.match_id}/${match.team1_name.toLowerCase().replace(/\s+/g, '-')}-vs-${match.team2_name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <strong title={match.team1_name}>{match.team1_shortcode}</strong>
                  <small className="text-muted px-1">[vs]</small>
                  <strong title={match.team2_name}>{match.team2_shortcode}</strong>
                </Link>
              </div>
              <div className="match-venue">{match.venue_name}{match.city ? `, ${match.city}` : ''}</div>
              <span className="pe-2 small">
                {match.team1_shortcode}: {parseInt(match.total_scored_runs_team1)}/{match.total_out_count_team1}
              </span>
              {match.team2_shortcode && (
                <span className="pe-2 small">
                  {match.team2_shortcode}: {parseInt(match.total_scored_runs_team2)}/{match.total_out_count_team2}
                </span>
              )}
            </div>
          </div>
        </div>
      </li>
    ));
  };

  return (
    <aside>
      <div className="card mt-4 match-fixtures shadow">
        <ul className="nav nav-pills mb-0" id="pills-tab" role="tablist">
          <li className="nav-item" role="presentation">
            <button className="nav-link" id="pills-upcoming-tab" data-bs-toggle="pill" data-bs-target="#pills-upcoming" type="button" role="tab" aria-controls="upcoming-home" aria-selected="true">Upcoming</button>
          </li>
          <li className="nav-item" role="presentation">
            <button className="nav-link active" id="pills-results-tab" data-bs-toggle="pill" data-bs-target="#pills-results" type="button" role="tab" aria-controls="pills-results" aria-selected="false">Results</button>
          </li>
        </ul>
        <div className="tab-content" id="pills-tabContent">
          <div className="tab-pane fade show" id="pills-upcoming" role="tabpanel" aria-labelledby="pills-upcoming-tab" tabIndex="0">
            <ul className="list-group list-group-flush">{renderUpcomingMatches()}</ul>
          </div>
          <div className="tab-pane fade show active" id="pills-results" role="tabpanel" aria-labelledby="pills-results-tab" tabIndex="0">
            <ul className="list-group list-group-flush">{renderMatches()}</ul>
          </div>
        </div>
        <div className="card-footer">
          <Link to="/fixtures-and-results">Fixtures & Results</Link>
        </div>
      </div>
      <div className="card mt-4 match-fixtures shadow">
        <ul className="nav nav-pills mb-0" id="pills-tab" role="tablist">
          <li className="nav-item" role="presentation">
            <button className="nav-link active" id="pills-runs-tab" data-bs-toggle="pill" data-bs-target="#pills-runs" type="button" role="tab" aria-controls="runs-results" aria-selected="true">Top Run Getters</button>
          </li>
          <li className="nav-item" role="presentation">
            <button className="nav-link" id="pills-wickets-tab" data-bs-toggle="pill" data-bs-target="#pills-wickets" type="button" role="tab" aria-controls="wickets-results" aria-selected="false">Top Wicket Takers</button>
          </li>
        </ul>
        <div className="tab-content" id="pills-tabContent">
          <div className="tab-pane fade show active" id="pills-runs" role="tabpanel" aria-labelledby="pills-runs-tab" tabIndex="0">
            <ul className="list-group list-group-flush">
              {topBatters.map((item, index) => (
                <li className={`list-group-item px-2 ${item.rank === '1' ? 'orange-cap' : ''}`} key={index}>
                  <div className="team-title">
                    <div className="d-flex align-items-top">
                      <div className="flex-shrink-0">
                        <div className="ranking-badge">{item.rank}</div>
                        <img src={`images/players/${item.full_name.toLowerCase().replace(/\s+/g, '-')}.webp`} alt={item.full_name} style={{ width: '65px' }} className="bg-white border border-primary-subtle rounded-circle" loading="lazy" />
                      </div>
                      <div className="flex-grow-2 ms-3">
                        <div>
                          <small>{item.team_shortcode}</small><br />
                          <strong title={item.full_name}>
                            <Link to={`${item.player_id}/${item.full_name.toLowerCase().replace(/\s+/g, '-')}`}>{item.full_name}</Link>
                          </strong>
                        </div>
                        <div className="match-venue">{item.total_runs_scored} Runs</div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="tab-pane fade show" id="pills-wickets" role="tabpanel" aria-labelledby="pills-wickets-tab" tabIndex="0">
            <ul className="list-group list-group-flush">
              {topBowlers.map((item, index) => (
                <li className={`list-group-item px-2 ${item.rank === '1' ? 'purple-cap' : ''}`} key={index}>
                  <div className="team-title">
                    <div className="d-flex align-items-top">
                      <div className="flex-shrink-0">
                        <div className="ranking-badge">{item.rank}</div>
                        <img
                        src={`images/players/${item.full_name.toLowerCase().replace(/\s+/g, '-')}.webp`}
                        alt={item.full_name}
                        style={{ width: '65px' }}
                        className="bg-white border border-primary-subtle rounded-circle"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null; // Prevents looping issues
                          e.target.src = 'images/players/default.webp'; // Fallback image path
                        }}
                      />
                      </div>
                      <div className="flex-grow-2 ms-3">
                        <div>
                          <small>{item.team_shortcode}</small><br />
                          <strong title={item.full_name}>
                            <Link to={`${item.player_id}/${item.full_name.toLowerCase().replace(/\s+/g, '-')}`}>{item.full_name}</Link>
                          </strong>
                        </div>
                        <div className="match-venue">{item.total_wickets_taken} Wickets</div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </aside>
  );
};

// Reusable Right Aside (Shared across Homepage and MatchDetails)
export const RenderRightAside = ({ todaySfs, pointsTable, playerBdays, posts }) => {
  const sliderSettings = {
    dots: true,
    infinite: playerBdays.length > 3,
    speed: 500,
    slidesToShow: Math.min(playerBdays.length || 1, 3),
    slidesToScroll: 1,
    autoplay: playerBdays.length > 1,
    autoplaySpeed: 2000,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: Math.min(playerBdays.length || 1, 2), slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } }
    ]
  };

  const renderPosts = () => {
      if (!posts?.length) {
        return null
      }
      return posts.map(item => (
        <div className="col-md-12 mb-4" key={item.id}>
          <div className="card shadow h-100">
            <img src={`https://wplcricinfo.com/wpl/admin/${item.imgUrl}`} className="card-img-top" alt={item.title} loading="lazy" />
            <div className="card-body p-2 h-100">
              <h3 className="card-title fs-6">
                <Link className="text-decoration-none" to={`/post/${item.id}/${CommonFunctions.formatPostTitleForLink(item.title)}`}>{item.title}</Link>
              </h3>
            </div>
            <div className="card-footer">
              <small className="text-body-secondary">Posted on: {CommonFunctions.getPostDateFormat(item.created_at)}</small>
            </div>
          </div>
        </div>
      ));
    };  

  const renderBirthdays = () => {
    if (playerBdays.length === 0) {
      return <div className="text-center text-white">No birthdays today!</div>;
    }
    
    return (
      <Slider {...sliderSettings}>
        {playerBdays.map((item, index) => {
          const firstName = item.first_name || 'Unknown';
          const lastName = item.last_name || 'Player';
          
          return (
            <div key={index}>
              <div className="d-flex justify-content-center mb-2">
                <img
                  src={`images/players/${firstName.toLowerCase()}-${lastName.toLowerCase()}.webp`}
                  alt={`${firstName} ${lastName}`}
                  style={{ width: '160px' }}
                  onError={(e) => { e.target.onerror = null; e.target.src = 'images/players/default.webp'; }}
                  loading="lazy"
                />
              </div>
              <div className="text-center text-white">
                <h4 className="fs-5 m-0">{firstName} {lastName}</h4>
                <small>{item.country || 'N/A'}</small><br />
                <small>{CommonFunctions.getPostDateFormat2(item.date_of_birth)}</small>
              </div>
            </div>
          );
        })}
      </Slider>
    );
  };

  return (
    <aside>
      <div className="card mt-4 mb-2">
        <div className="card-body p-0">
          <div className="card-title px-3 pt-2 text-center">WPL 2025 - 6's & 4's</div>
          <div className="row m-0">
            <div className="col-sm-6 p-0">
              <div className="total-sixes text-center text-white fs-2">{todaySfs?.total_sixes || '0'}</div>
              <div className="total-sixes text-center text-white"><small>Total Sixes</small></div>
            </div>
            <div className="col-sm-6 p-0">
              <div className="total-fours text-center text-white fs-2">{todaySfs?.total_fours || '0'}</div>
              <div className="total-fours text-center text-white"><small>Total Fours</small></div>
            </div>
          </div>
        </div>
      </div>
      <div className="card team-table shadow">
        <div className="card-body p-0">
          <div className="card-title px-2 pt-2">Women's Premier League Table 2025</div>
          <div className="table-responsive table-sm">
            <table className="table table-bordered table-striped mb-0">
              <thead>
                <tr><th>TEAM</th><th>M</th><th>W</th><th>L</th><th>PT</th><th>NRR</th></tr>
              </thead>
              <tbody>
                {pointsTable.map((items, index) => (
                  <tr key={index}>
                    <td>{items.team}</td>
                    <td>{items.m}</td>
                    <td>{items.w}</td>
                    <td>{items.l}</td>
                    <td>{items.pt}</td>
                    <td>{items.nrr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="bday-slider my-2">
        <div className="card shadow">
          <div className="card-title text-center text-white m-0 bg-info">HAPPY BIRTHDAY !!</div>
          <div className="card-body p-0 pb-5">
            <Suspense fallback={<div>Loading Birthdays...</div>}>
              {renderBirthdays()}
            </Suspense>
          </div>
        </div>
      </div>
        {renderPosts()}
    </aside>
  );
};

// Reusable Top Players Section (Shared between Homepage and FixturesAndResults)
// export const RenderTopPlayers = ({ topBatters, topBowlers }) => {
//   return (
//     <div className="card mt-4 match-fixtures shadow">
//       <ul className="nav nav-pills mb-0" id="pills-tab" role="tablist">
//         <li className="nav-item" role="presentation">
//           <button className="nav-link active" id="pills-runs-tab" data-bs-toggle="pill" data-bs-target="#pills-runs" type="button" role="tab" aria-controls="runs-results" aria-selected="true">Top Run Getters</button>
//         </li>
//         <li className="nav-item" role="presentation">
//           <button className="nav-link" id="pills-wickets-tab" data-bs-toggle="pill" data-bs-target="#pills-wickets" type="button" role="tab" aria-controls="wickets-results" aria-selected="false">Top Wicket Takers</button>
//         </li>
//       </ul>
//       <div className="tab-content" id="pills-tabContent">
//         <div className="tab-pane fade show active" id="pills-runs" role="tabpanel" aria-labelledby="pills-runs-tab" tabIndex="0">
//           <ul className="list-group list-group-flush">
//             {topBatters?.map((item, index) => (
//               <li className={`list-group-item px-2 ${item.rank === '1' ? 'orange-cap' : ''}`} key={index}>
//                 <div className="team-title">
//                   <div className="d-flex align-items-top">
//                     <div className="flex-shrink-0">
//                       <div className="ranking-badge">{item.rank}</div>
//                       <img
//                         src={`images/players/${item.full_name.toLowerCase().replace(/\s+/g, '-')}.webp`}
//                         alt={item.full_name}
//                         style={{ width: '65px' }}
//                         className="bg-white border border-primary-subtle rounded-circle"
//                         loading="lazy"
//                         onError={(e) => {
//                           e.target.onerror = null; // Prevents looping issues
//                           e.target.src = 'images/players/default.webp'; // Fallback image path
//                         }}
//                       />
//                     </div>
//                     <div className="flex-grow-2 ms-3">
//                       <div>
//                         <small>{item.team_shortcode}</small><br />
//                         <strong title={item.full_name}>
//                           <Link to={`/${item.player_id}/${item.full_name.toLowerCase().replace(/\s+/g, '-')}`}>{item.full_name}</Link>
//                         </strong>
//                       </div>
//                       <div className="match-venue">{item.total_runs_scored} Runs</div>
//                     </div>
//                   </div>
//                 </div>
//               </li>
//             ))}
//           </ul>
//         </div>
//         <div className="tab-pane fade" id="pills-wickets" role="tabpanel" aria-labelledby="pills-wickets-tab" tabIndex="0">
//           <ul className="list-group list-group-flush">
//             {topBowlers?.map((item, index) => (
//               <li className={`list-group-item px-2 ${item.rank === '1' ? 'purple-cap' : ''}`} key={index}>
//                 <div className="team-title">
//                   <div className="d-flex align-items-top">
//                     <div className="flex-shrink-0">
//                       <div className="ranking-badge">{item.rank}</div>
//                       <img
//                         src={`images/players/${item.full_name.toLowerCase().replace(/\s+/g, '-')}.webp`}
//                         alt={item.full_name}
//                         style={{ width: '65px' }}
//                         className="bg-white border border-primary-subtle rounded-circle"
//                         loading="lazy"
//                         onError={(e) => {
//                           console.error('Image failed to load: ', e.target.src);
//                           e.target.onerror = null; // Prevents looping issues
//                           e.target.src = 'images/players/default.webp'; // Fallback image path
//                         }}
//                       />
//                     </div>
//                     <div className="flex-grow-2 ms-3">
//                       <div>
//                         <small>{item.team_shortcode}</small><br />
//                         <strong title={item.full_name}>
//                           <Link to={`/${item.player_id}/${item.full_name.toLowerCase().replace(/\s+/g, '-')}`}>{item.full_name}</Link>
//                         </strong>
//                       </div>
//                       <div className="match-venue">{item.total_wickets_taken} Wickets 33</div>
//                     </div>
//                   </div>
//                 </div>
//               </li>
//             ))}
//           </ul>
//         </div>
//       </div>
//     </div>
//   );
// };